import { Link } from 'react-router-dom';
import { useThemeStore, isDarkChromeTheme, type ThemeId } from '@/entities/session/model/themeStore';
import { cn } from '@/shared/lib/cn';

export interface BrandWordmarkProps {
  className?: string;
  /** Высота логотипа (Tailwind), по умолчанию h-8 */
  heightClass?: string;
  /** Ссылка (например /app); без неё — декоративный блок */
  to?: string;
  /** Всегда вариант для светлого фона (страницы логина и т.п.) */
  forceLight?: boolean;
  /**
   * Фон под логотипом: тёмный → светлый «ARTET» (`wordmark-duo-dark.svg`), светлый → тёмный «ARTET» (`wordmark-duo.svg`).
   * Задавайте явно в шапке и сайдбаре: у Ultra шапка светлая, колонка тёмная — варианты различаются.
   */
  onDarkSurface?: boolean;
  /** Центрировать вордмарк по горизонтали (шапка/сайдбар — по умолчанию выравнивание по началу) */
  centered?: boolean;
}

function pickSrc(theme: ThemeId, forceLight: boolean, onDarkSurface: boolean | undefined): string {
  if (forceLight) {
    return '/brand/wordmark-duo.svg';
  }
  const dark =
    onDarkSurface !== undefined ? onDarkSurface : isDarkChromeTheme(theme);
  return dark ? '/brand/wordmark-duo-dark.svg' : '/brand/wordmark-duo.svg';
}

/**
 * Вордмарк QWARTET: светлый фон → `/brand/wordmark-duo.svg` (тёмный «ARTET»);
 * тёмная поверхность → `wordmark-duo-dark.svg`. Передайте `onDarkSurface` в шапке/сайдбаре — иначе Ultra даст бледный логотип на светлой шапке.
 */
export function BrandWordmark({
  className,
  heightClass = 'h-8',
  to,
  forceLight = false,
  onDarkSurface,
  centered = false,
}: BrandWordmarkProps) {
  const theme = useThemeStore((s) => s.theme);
  const src = pickSrc(theme, forceLight, onDarkSurface);

  const img = (
    <img
      src={src}
      alt=""
      width={168}
      height={40}
      decoding="async"
      className={cn(
        'block h-auto w-auto max-w-full object-contain object-left',
        heightClass,
        className,
      )}
    />
  );

  const wrapClass = centered
    ? 'mx-auto flex w-fit max-w-full min-w-0 justify-center'
    : 'inline-flex w-fit min-w-0 max-w-full items-center';

  const labeled = (
    <span className={wrapClass} role="img" aria-label="QWARTET">
      {img}
    </span>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          'rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
          centered ? 'mx-auto flex w-fit max-w-full min-w-0 justify-center' : 'inline-flex w-fit min-w-0 max-w-full items-center',
        )}
        aria-label="QWARTET"
      >
        {img}
      </Link>
    );
  }

  return labeled;
}
